import {Avatar, Modal} from 'antd'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState,} from 'react'
import {useLocation} from 'react-use'
import {DelayRender} from 'views/Shared/components/DelayRender'
import {NullAsDefault} from 'views/Shared'
import {bindQueryParam} from "../../functions/routerHelper";
import createContextMenu from "../../factory/createContextMenu";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import usePaginationHelper, {createPagerKey} from "../../hooks/usePaginationHelper";
import _ from "lodash";
import CRUD from "../../../Shared/enums/CRUD";
import {RiSearchEyeLine} from "react-icons/ri";
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import {HiPlus} from "react-icons/hi2";
import {getRouteSearchParams, useRouteSearchParam} from "../../../../envs/_current/history";
import {useCatholicHouseholdMembers} from "../functions/useCatholicHouseholdMembers";
import {catholicHousehold_getCatholicHouseholdMembers_Api} from "../../../../apis/catholicHousehold";
import {renderCatholicHouseholdMember} from "../functions/renderCatholicHouseholdMember";
import {
    CreateCatholicHouseholdMemberForm,
    DetailViewForm,
    EditCatholicHouseholdMemberForm
} from "../components/CatholicHouseholdMemberForm";
import getTitle from "../../../../helpers/getTitle";
import {ItemInformation} from 'components/common/ItemInformation'
import LoadingBlock from "../../../Loading/LoadingBlock";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";

const CatholicHouseholdMembers = ({
                                      item,
                                      Wrapper = 'div',
                                      handleAction = NullAsDefault,
                                      filter_actions = {
                                          sortBy: 'sort-by',
                                          keyword: 'keyword'
                                      },
                                  }) => {

    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const location = useLocation()
    const searchParams = getRouteSearchParams()
    const [keyword, setKeyword] = useState(searchParams.get("keyword") || "")

    const _debouncedHandleSearch = _.debounce(
        (value) => {
            setKeyword(value)
        },
        200
    )

    const [refreshKey, setRefreshKey] = useState()
    const [action, setAction] = useState({type: undefined, value: undefined})

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const refresh_deps = useMemo(
        () => [
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [refreshKey, location.state]
    )

    const onCancel = () => {
        setAction({})
    }

    const onSuccess = () => {
        onCancel()
        setRefreshKey(Date.now())
    }

    const {onCreate, onUpdate, onDelete} = useCatholicHouseholdMembers({
        catholic_household_id: item?.id,
        notifyListeners: {
            onCreated: onSuccess,
            onUpdated: onSuccess,
            onDeleted: onSuccess,
        },
    })

    const renderContextMenu = useCallback((item) => {
        return createContextMenu(
            [
                {
                    icon: <RiSearchEyeLine size={14} className="text-color-400"/>,
                    label: <span className="text-sm text-color-200">{t('view information')}</span>,
                    key: CRUD.READ,
                },
                {
                    icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                    label: <span className="text-sm text-color-200">{t('edit')}</span>,
                    key: CRUD.UPDATE,
                    invisible: !!!item?.edit
                },
                {
                    icon: <BiTrash size={14} className="text-primary-300"/>,
                    label: <span className="text-sm text-color-200">{t('delete')}</span>,
                    key: CRUD.DELETE,
                    invisible: !!!item?.edit
                },
            ].filter(e => item && (!!!e.invisible)),
            (key) => {
                switch (key) {
                    case CRUD.READ:
                        setAction({type: key, value: item})
                        break
                    case CRUD.UPDATE:
                        setAction({type: key, value: item})
                        break
                    case CRUD.DELETE:
                        const id = item?.id
                        if (id) {
                            showDeleteConfirmDialog({
                                translate: t,
                                title: 'delete this member',
                                message: 'are you sure you want to delete this member',
                                onOk: () => {
                                    onDelete(
                                        {},
                                        {
                                            id,
                                        }
                                    )
                                },
                            })
                        }
                        break
                    default:
                        break
                }
            }
        )
    }, [])

    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        renderFilter: NullAsDefault,
        isToggleDefault: true,
        AdvancedFilter: NullAsDefault,
        withValues: (values) => ({
            ...values,
        }),
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            query: bindQueryParam({id: item?.id}),
            apiInfo: catholicHousehold_getCatholicHouseholdMembers_Api,
            renderItem: renderCatholicHouseholdMember({
                translate: t,
                isLg: isLg,
                renderContextMenu,
                handleAction: (type, value) => {
                    setAction({type, value});
                },
            }),
            renderWrap: (child) => (
                <div className="mt-2 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            )
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onSortChange(sortBy)
            onFilterChange({})
        },
        filter_params: {},
        dependencies: [],
        renderHeader: ([searchBar, filterBar]) => (
            <div className="mt-3 flex items-center gap-4">
                <div className="flex flex-col flex-1">
                    <span className="font-medium text-sm text-gray-700 tracking-wide">
                        {t('list of members')}
                    </span>
                    <div style={{height: 3}} className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
                <div
                    onClick={() => {
                        setAction({type: CRUD.CREATE, value: item})
                    }}
                    style={{padding: '4px 12px'}}
                    className="flex items-center gap-1 bg-primary-50 hover:bg-primary-100 rounded-md cursor-pointer">
                    <HiPlus size={11} className="text-primary"/>
                    <span className="font-medium text-xs text-primary">
                        {t('add member')}
                    </span>
                </div>
            </div>
        ),
    })

    const modal = useMemo(() => {
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({initialValues: {}})
        let params = {}
        let modal_params = {}

        switch (action.type) {
            case CRUD.READ:
                modal_params = {
                    width: 560,
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('member information')}
                        </div>
                    ),
                }
                params = {
                    item: action?.value,
                    catholicHousehold: item,
                    onCancel,
                }
                Content = ({...rest}) => (
                    <DetailViewForm
                        {...rest}
                        item={action.value}
                        catholicHousehold={item}
                    />
                )
                break
            case CRUD.CREATE:
                params = {
                    catholicHousehold: action?.value,
                    onSubmit: (values) => {
                        onCreate(values)
                    },
                }
                modal_params = {
                    width: 740,
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('add member')}
                        </div>
                    ),
                }
                Content = CreateCatholicHouseholdMemberForm
                break
            case CRUD.UPDATE:
                params = {
                    onSubmit: (values) => {
                        onUpdate(values, {id: action.value?.id})
                    },
                }
                modal_params = {
                    width: 740,
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('edit member')}
                        </div>
                    ),
                }
                Content = ({...rest}) => (
                    <EditCatholicHouseholdMemberForm
                        {...rest}
                        initialValues={action.value}
                    />
                )
                break
            default:
                break
        }

        return (
            <Modal
                className="custom-modal"
                footer={null}
                onCancel={onCancel}
                open={action.type}
                destroyOnHidden={true}
                styles={{body: {padding: 0}}}
                {...modal_params}>
                <DelayRender lazy={true}>
                    <Wrapper>
                        {({isLoading, initialValues}) => {
                            if (!!isLoading) {
                                return <LoadingBlock/>
                            }
                            return (
                                <Content
                                    {...{
                                        onCancel,
                                        item: action.value,
                                    }}
                                    {...params}
                                    initialValues={initialValues}
                                />
                            )
                        }}
                    </Wrapper>
                </DelayRender>
            </Modal>
        )
    }, [action])

    return (
        <Wrapper className="mt-3 flex flex-col gap-3 px-2 md:px-4 container mx-auto">
            <div className="flex items-center gap-3 border border-color-50">
                <Avatar
                    size={80}
                    src="/images/catholic-household-banner.webp"
                    className="flex flex-center background-200 rounded-l-lg rounded-r-0 opacity-75"
                />
                <div className="flex flex-col gap-1">
                    <span className="font-bold text-color-000">
                        {`${t('catholic household')} ${getTitle(item)}`}
                    </span>
                    <ItemInformation
                        showIcon={true} className="text-xs italic" label="number of households"
                        value={item?.source_idcode}
                    />
                    <ItemInformation
                        showIcon={true} className="text-xs italic" label="belong to"
                        value={_.get(item, 'parish.fullname')}
                    />
                </div>
            </div>
            {pager_widget}
            {modal}
            <div className="h-16"/>
        </Wrapper>
    )
}


export default CatholicHouseholdMembers
